import React, { useMemo } from "react";
import { LayoutGrid, ListFilter } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/primitives/select";
import { cn } from "@/lib/utils";

function WorkspaceOption({ icon: Icon = LayoutGrid, label }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {React.createElement(Icon, { "aria-hidden": true, className: "size-4 shrink-0" })}
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

export function resolveSidebarWorkspaceSelection({
  modules = [],
  customWorkspaces = [],
  activeWorkspace = "all",
  t,
}) {
  const allLabel = t("commandCenter.modules.items.all", {
    ns: "header",
    defaultValue: "All modules",
  });
  const options = [
    { key: "all", label: allLabel, icon: LayoutGrid },
    ...modules.map((module) => ({
      key: module.key,
      label: t(`commandCenter.modules.items.${module.key}`, {
        ns: "header",
        defaultValue: module.defaultLabel,
      }),
      icon: module.icon || LayoutGrid,
    })),
    ...customWorkspaces.map((workspace) => ({
      key: workspace.id,
      label: workspace.name,
      icon: LayoutGrid,
    })),
  ];
  const selectedKey = options.some(({ key }) => key === activeWorkspace)
    ? activeWorkspace
    : "all";
  const selectedOption = options.find(({ key }) => key === selectedKey) || options[0];
  const selectedLabel = selectedOption?.label || allLabel;
  return {
    options,
    allLabel,
    selectedKey,
    selectedLabel,
    isFiltered: selectedKey !== "all",
  };
}

export function SidebarWorkspaceFilterCaption({
  label,
  isFiltered = false,
  className,
}) {
  if (!isFiltered || !label) return null;
  return (
    <p
      className={cn(
        "min-w-0 truncate text-center text-xs font-bold leading-tight text-sidebar-primary",
        className,
      )}
      data-sidebar-filter-caption
      data-filtered="true"
      title={label}
    >
      {label}
    </p>
  );
}

export function SidebarWorkspaceSelector({
  modules = [],
  customWorkspaces = [],
  activeWorkspace = "all",
  onSelectWorkspace,
  isCollapsed = false,
  disabled = false,
}) {
  const { t } = useTranslation(["sidebar", "header"]);
  const { options, selectedKey, selectedLabel, isFiltered } = useMemo(
    () => resolveSidebarWorkspaceSelection({
      modules,
      customWorkspaces,
      activeWorkspace,
      t,
    }),
    [activeWorkspace, customWorkspaces, modules, t],
  );
  const selectLabel = isFiltered
    ? t("workspace.filterActive", {
        defaultValue: "Module filter: {{name}}",
        name: selectedLabel,
      })
    : t("workspace.filter", {
        defaultValue: "Filter modules",
      });

  return (
    <div
      className={cn(
        "shrink-0",
        isCollapsed ? "flex justify-center px-1.5 py-2" : undefined,
      )}
      data-sidebar-workspace-filter={isFiltered ? "active" : "idle"}
    >
      <Select
        value={selectedKey}
        onValueChange={onSelectWorkspace}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={selectLabel}
          title={selectLabel}
          data-filtered={isFiltered ? "true" : "false"}
          className={cn(
            "relative size-[var(--ds-density-control-height)] shrink-0 justify-center gap-0 px-0 [&>svg:last-child]:hidden",
            isFiltered
              ? "border-sidebar-primary/50 bg-sidebar-primary/15 text-sidebar-primary shadow-none hover:bg-sidebar-primary/20 hover:text-sidebar-primary"
              : "border-sidebar-border bg-sidebar/80 text-sidebar-foreground/70 shadow-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          )}
        >
          <ListFilter aria-hidden="true" className="size-4 shrink-0" />
          {isFiltered ? (
            <span
              aria-hidden="true"
              className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-sidebar-primary"
            />
          ) : null}
          <SelectValue className="sr-only" placeholder={selectedLabel} />
        </SelectTrigger>
        <SelectContent
          align={isCollapsed ? "start" : "end"}
          className="min-w-[var(--ds-overlay-popover-width)]"
        >
          {options.map((option) => (
            <SelectItem
              key={option.key}
              value={option.key}
              displayValue={option.label}
              textValue={option.label}
              renderContent
            >
              <WorkspaceOption icon={option.icon} label={option.label} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
